import type { TriggerSummary } from './hooks';

/**
 * A trigger's health, worst-first: broken, then can't-run-anything (nothing
 * attached), then attached-but-never-fired, then working.
 *
 * Shared so the Status column and the health filter can never disagree about
 * what a trigger's state is — the derivation used to live inside the row that
 * rendered it, which put it out of reach of the page that does the filtering.
 */
export type TriggerHealth = 'failing' | 'idle' | 'ready' | 'active';

export const TRIGGER_HEALTH_LABEL: Record<TriggerHealth, string> = {
  failing: 'Failing',
  idle: 'Idle',
  ready: 'Ready',
  active: 'Active',
};

export function triggerHealth(params: {
  hasError: boolean;
  workflowCount: number;
  lastFiredAt: Date | null;
}): TriggerHealth {
  if (params.hasError) {
    return 'failing';
  }
  if (params.workflowCount === 0) {
    return 'idle';
  }
  return params.lastFiredAt === null ? 'ready' : 'active';
}

/**
 * Whether a trigger passes the health filter.
 *
 * An empty set passes everything, so callers don't each need their own "is the
 * filter even on" guard — the check that used to sit inline in the page was
 * nested under a search-query early return, which meant picking a status did
 * nothing at all until you also typed in the search box.
 */
export function matchesHealthFilter(
  selected: ReadonlySet<TriggerHealth>,
  summary: TriggerSummary | undefined
): boolean {
  return selected.size === 0 || selected.has(triggerHealthFromSummary(summary));
}

/** Health for a trigger whose rollup hasn't arrived yet — treated as unattached. */
export function triggerHealthFromSummary(
  summary: TriggerSummary | undefined
): TriggerHealth {
  return triggerHealth({
    hasError: summary?.hasError ?? false,
    workflowCount: summary?.workflowCount ?? 0,
    lastFiredAt: summary?.lastFiredAt ?? null,
  });
}
